import * as React from 'react';

import { Break, ProviderSchedules } from '../../redux/providers/types';
import { ProviderData } from '../../api/providers.types';
import { ICommandBarItemProps, Stack, TextField } from '@fluentui/react';

import { AsyncData } from '../../redux/core';
import { BreakDialog } from './dialogs/BreakDialog';
import { MarketSelector } from '../map/MarketSelector';
import { PageHeader } from '../core/PageHeader';
import { PageSidePanel } from '../core/PageSidePanel';
import { Visit } from '../../redux/visits/types';
import { VisitDateSelector } from './VisitDateSelector';
import { VisitDetailsList } from './VisitDetailsList';
import { VisitSidePanel } from './VisitSidePanel';
import { VisitStatusSelector } from './VisitStatusSelector';
import TrieSearch from 'trie-search';
import { ServiceSelector } from '../map/ServiceSelector';
import { Market } from '../../api/markets.types';
import CancelUnconfirmedVisitsDialog from './dialogs/CancelUnconfirmedVisitsDialog';
import { ToastParams } from '../../redux/message/types';

export interface OwnProps {}

export interface StateProps {
  markets: AsyncData<Market[]>;
  marketCodes: string[];
  serviceTypes: string[];
  date: string;
  visits: AsyncData<Visit[]>;
  visitCodeToVisitIndex: { [code: string]: number };
  marketToBreaks: { [marketCode: string]: AsyncData<Break[]> };
  visitFilterText: string;
  visitCode: string;
  marketToProviderSchedules: { [marketCode: string]: AsyncData<ProviderSchedules[]> };
  allProviders: AsyncData<ProviderData[]>;
}

export interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    setVisitCode: (code: string) => void;
    setVisitFilterText: (text: string) => void;
    createBreak: (doctorId: string, startTime: number, endTime: number, type: string, MAId?: string) => void;
    editBreak: (id: string, doctorId: string, startTime: number, endTime: number, type: string, MAId?: string) => void;
    deleteBreak: (breakId: string) => void;
    toast: (params: ToastParams) => void;
    refreshVisits: () => void;
  };
}

type VisitsProps = OwnProps & StateProps & DispatchProps;

export interface VisitsState {
  filteredItems: (Visit | Break)[];
  items: (Visit | Break)[];
  markets: Market[];
  showCancelUnconfirmedVisitsDialog: boolean;
}

export class Visits extends React.Component<VisitsProps, VisitsState> {
  private trie: any;

  constructor(props: VisitsProps) {
    super(props);
    this.state = {
      filteredItems: [],
      items: [],
      markets: this.props.markets.data ? this.props.markets.data.filter((s) => this.props.marketCodes.some((m) => m === s.code)) : [],
      showCancelUnconfirmedVisitsDialog: false,
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Visits');
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Visits');
  }

  public componentDidUpdate(prevProps: VisitsProps) {
    if (prevProps.markets.data !== this.props.markets.data || prevProps.marketCodes !== this.props.marketCodes) {
      this.setState({
        markets: this.props.markets.data ? this.props.markets.data.filter((s) => this.props.marketCodes.some((m) => m === s.code)) : [],
      });
    }

    if (
      this.props.serviceTypes !== prevProps.serviceTypes ||
      this.props.visits !== prevProps.visits ||
      this.props.marketToBreaks !== prevProps.marketToBreaks ||
      this.props.marketCodes !== prevProps.marketCodes
    ) {
      const breaks: Break[] = [];
      const visits = this.props.visits.data?.filter(
        (i: Visit) => (this.props.serviceTypes.length && this.props.serviceTypes[0] === 'ALL') || this.props.serviceTypes.indexOf(i.visitServiceCode) > -1
      );
      this.trie = new TrieSearch(['visitCode', 'buildPatientFullName', 'buildDoctorFullName', 'buildAssistantFullName', 'visitServiceName']);

      for (const market of this.props.marketCodes) {
        if (this.props.marketToBreaks[market] && this.props.marketToBreaks[market].data) {
          for (const singleBreak of this.props.marketToBreaks[market].data!) {
            if (singleBreak && singleBreak.endTime + 60 * 60 * 1000 > new Date().getTime()) {
              breaks.push(singleBreak);
            }
          }
        }
      }
      if (visits) {
        this.trie.addAll(visits);
      }
      if (breaks) {
        this.trie.addAll(breaks);
      }

      this.setState({ filteredItems: [...(visits || []), ...breaks], items: [...(visits || []), ...breaks] });
    }

    if (prevProps.visitFilterText !== this.props.visitFilterText) {
      if (this.props.visitFilterText) {
        this.setState({ filteredItems: this.trie.get(this.props.visitFilterText) });
      } else {
        this.setState({ filteredItems: this.state.items.slice() });
      }
    }
  }

  public render(): JSX.Element {
    const items: ICommandBarItemProps[] = [
      {
        key: 'createBreak',
        text: 'Create break',
        iconProps: { iconName: 'Add' },
        onClick: () =>
          BreakDialog.show({
            isEdit: false,
            uiDate: this.props.date,
            marketToProviderSchedules: this.props.marketToProviderSchedules,
            createBreak: this.props.actions.createBreak,
            markets: this.props.markets.data!,
          }),
      },
      // {
      //   key: 'cancelUnconfirmedVisits',
      //   text: 'Cancel unconfirmed visits',
      //   iconProps: { iconName: 'EventDeclined' },
      //   onClick: () => this.setState({ showCancelUnconfirmedVisitsDialog: true }),
      //   disabled: !moment(this.props.date).isBetween(moment(), moment().add(1, 'day'), 'day', '[]'),
      //   onRender: (item: any) => (
      //     <TooltipHost
      //       content="Manually cancel visits that are approved but not confirmed. Only available when date is set to today or tomorrow."
      //       style={{ display: 'inline-block', height: '100%' }}
      //     >
      //       <CommandBarButton {...item} style={{ height: '100%' }} />
      //     </TooltipHost>
      //   ),
      // },
    ];

    return (
      <>
        <PageHeader style={{ marginBottom: 0 }} title="Visits" isLoading={this.props.visits.isFetching || this.props.markets.isFetching} actions={{ items }} />
        <Stack horizontal={true} verticalAlign="end" tokens={{ childrenGap: 10 }}>
          <MarketSelector />
          <ServiceSelector />
          <VisitStatusSelector />
          <VisitDateSelector defaultDate={this.props.date} />
          {this.renderFilterControl()}
          {this.renderResultsCount()}
        </Stack>
        <VisitDetailsList
          items={this.state.filteredItems}
          date={this.props.date}
          markets={this.state.markets}
          visitCode={this.props.visitCode}
          actions={this.props.actions}
          allProviders={this.props.allProviders}
          marketToProviderSchedules={this.props.marketToProviderSchedules}
        />
        {this.renderSidePanel()}

        <CancelUnconfirmedVisitsDialog
          hidden={!this.state.showCancelUnconfirmedVisitsDialog}
          onCancel={() => this.setState({ showCancelUnconfirmedVisitsDialog: false })}
          onSuccess={() => {
            this.setState({ showCancelUnconfirmedVisitsDialog: false });
            this.props.actions.refreshVisits();
          }}
        />
      </>
    );
  }

  private renderFilterControl(): JSX.Element | null {
    if (!this.state.markets || this.state.markets.length === 0 || !this.props.visits.data || this.props.visits.data.length === 0) {
      return null;
    }
    return (
      <div style={{ marginLeft: 'auto', marginRight: 10 }}>
        <TextField iconProps={{ iconName: 'Filter' }} placeholder="Filter visits" value={this.props.visitFilterText} onChange={this.onSearchChange} />
      </div>
    );
  }

  private renderResultsCount(): JSX.Element | null {
    if (!this.state.markets || this.state.markets.length === 0 || !this.props.visits.data || this.props.visits.data.length === 0) {
      return null;
    }

    const visible = this.state.filteredItems.filter((i) => !!(i as Visit).visitCode).length;
    const actual = this.props.visits.data.length;
    if (visible === actual) {
      return <span style={{ fontSize: 20 }}> Results: {visible}</span>;
    } else {
      return (
        <span style={{ fontSize: 20 }}>
          {' '}
          Results: {visible}/{actual}
        </span>
      );
    }
  }

  private onSearchChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    this.props.actions.setVisitFilterText(newValue || '');
  };

  private renderSidePanel(): JSX.Element | null {
    if (!this.props.visitCode || !this.props.visits.data) {
      return null;
    }
    const index = this.props.visitCodeToVisitIndex[this.props.visitCode];
    const visit = this.props.visits.data[index];
    if (!visit) {
      return null;
    }

    return (
      <PageSidePanel>
        <VisitSidePanel visit={visit} />
      </PageSidePanel>
    );
  }
}
